import React, { useMemo, useReducer, useState } from 'react';
import {
  FiAlertCircle,
  FiBookOpen,
  FiCloud,
  FiCode,
  FiCpu,
  FiDatabase,
  FiGlobe,
  FiGrid,
  FiLayers,
  FiMessageSquare,
  FiTool,
} from 'react-icons/fi';
import { Navbar, Breadcrumbs } from '../components';
import { Footer } from '../containers';
import SEOMeta from '../components/SEOMeta/SEOMeta';
import { generateBreadcrumbSchema } from '../utils/seoSchema';
import {
  LEARN_TREE,
  TOPIC_LOOKUP,
  HTML_LESSONS,
  HTML_INTERVIEW_QNA,
  CSS_LESSONS,
  CSS_INTERVIEW_QNA,
  JS_LESSONS,
  JS_INTERVIEW_QNA,
} from '../data/learnCurriculum';
import './LearnPage.css';

const buildInitialExpandedMap = () => LEARN_TREE.reduce((acc, tech, index) => {
  acc[tech.id] = index === 0;
  return acc;
}, {});

const TECH_ICON_MAP = {
  html: FiGlobe,
  css: FiLayers,
  javascript: FiCode,
  java: FiCode,
  'springboot-microservices': FiCloud,
  typescript: FiCode,
  react: FiCpu,
  nextjs: FiCloud,
  angular: FiGrid,
  'node-express': FiTool,
  mongodb: FiDatabase,
  'aws-devops': FiCloud,
  'system-design': FiCpu,
};

const IMPORTANT_TOPIC_IDS = new Set([
  'html-seo-basics',
  'html-accessibility',
  'html-interview-qna',
  'css-specificity',
  'css-flexbox',
  'css-grid',
  'css-interview-qna',
  'java-oops-pillars',
  'java-collections-overview',
  'java-streams-api',
  'java-exception-handling',
  'java-concurrency-api',
  'java-design-patterns-creational',
  'java-design-patterns-structural',
  'java-design-patterns-behavioral',
  'java-interview-qna',
  'sbm-microservices-fundamentals',
  'sbm-service-discovery',
  'sbm-api-gateway',
  'sbm-resilience-patterns',
  'sbm-saga-distributed-transactions',
  'sbm-event-driven-architecture',
  'sbm-microservices-security',
  'sbm-interview-qna',
  'sd-high-level-design',
  'sd-low-level-design',
  'sd-consistency-availability',
  'sd-load-balancers',
  'sd-message-queues',
  'sd-monolith-vs-microservices',
  'sd-system-design-tradeoffs',
  'sd-design-patterns',
  'sd-concurrency-thread-safety',
  'sd-common-lld-problems',
  'sd-interview-qna',
]);

const flattenReferenceSections = (sections = []) => sections.flatMap((section) => {
  const notes = section.notes || [];
  const rows = section.rows || [];
  return [
    section.title,
    section.intro || '',
    ...notes,
    ...rows.flatMap((row) => Object.values(row)),
  ];
});

const INITIAL_STATE = {
  expandedTechMap: buildInitialExpandedMap(),
  selectedTechId: 'html',
  selectedTopicId: 'html-introduction',
};

const DEFAULT_TOPIC_ID = 'html-introduction';

const getTopicIdFromLocation = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  // Accept both /learn/topic/:id route and legacy ?topic=id links.
  const pathMatch = window.location.pathname.match(/^\/learn\/topic\/([^/?#]+)/i);
  if (pathMatch?.[1]) {
    return decodeURIComponent(pathMatch[1]);
  }
  const queryTopicId = new URLSearchParams(window.location.search).get('topic');
  return queryTopicId || null;
};

const getInitialTopicId = () => {
  const locationTopicId = getTopicIdFromLocation();
  return locationTopicId && TOPIC_LOOKUP[locationTopicId] ? locationTopicId : DEFAULT_TOPIC_ID;
};

const learnReducer = (state, action) => {
  switch (action.type) {
    case 'TOGGLE_TECH': {
      return {
        ...state,
        expandedTechMap: {
          ...state.expandedTechMap,
          [action.techId]: !state.expandedTechMap[action.techId],
        },
      };
    }
    case 'SELECT_TOPIC': {
      return {
        ...state,
        selectedTechId: action.techId,
        selectedTopicId: action.topicId,
        expandedTechMap: {
          ...state.expandedTechMap,
          [action.techId]: true,
        },
      };
    }
    default:
      return state;
  }
};

const toSrcDoc = (code) => {
  const trimmed = (code || '').trim();
  if (trimmed.toLowerCase().includes('<html')) {
    return trimmed;
  }
  return `<!doctype html><html><body style="font-family:Arial, sans-serif;padding:12px;color:#111;line-height:1.6;">${trimmed}</body></html>`;
};

const EditableExample = ({ example, index }) => {
  const [code, setCode] = useState(example.code);

  React.useEffect(() => {
    setCode(example.code);
  }, [example.code]);

  return (
    <article className="learn-example-card" key={`${example.title}-${index}`}>
      <h4>{example.title}</h4>
      <p>{example.description}</p>
      <div className="learn-example-grid">
        <div>
          <div className="learn-code-title">Editable Code</div>
          <textarea
            className="learn-code-editor"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            spellCheck={false}
          />
        </div>
        <div>
          <div className="learn-code-title">Live Output</div>
          <iframe
            className="learn-output-frame"
            title={`${example.title}-${index}-output`}
            // allow-same-origin is required for storage/cache APIs in srcDoc examples.
            sandbox="allow-scripts allow-same-origin"
            srcDoc={toSrcDoc(code)}
          />
        </div>
      </div>
    </article>
  );
};

const LessonExamples = ({ examples = [] }) => (
  <section className="learn-examples">
    <h3>Practice Zone</h3>
    <p>Edit the code and instantly see the output side-by-side.</p>
    {examples.map((example, index) => (
      <EditableExample key={`${example.title}-${index}`} example={example} index={index} />
    ))}
  </section>
);

const formatReferenceCell = (value) => {
  if (value === 'Yes' || value === 'No' || value === 'Partial' || value === 'Deprecated') {
    return <span className={`learn-badge ${value.toLowerCase()}`}>{value}</span>;
  }
  return value;
};

const LessonReferenceSections = ({ sections = [] }) => (
  <section className="learn-reference-wrap">
    {sections.map((section) => (
      <article className="learn-reference-card" key={section.title}>
        <h3>{section.title}</h3>
        {section.intro ? <p>{section.intro}</p> : null}
        <div className="learn-table-scroll">
          <table className="learn-reference-table">
            <thead>
              <tr>
                {section.columns.map((column) => (
                  <th key={column.key}>
                    {column.icon ? (
                      <span className={`learn-browser-chip ${column.icon}`}>{column.iconLabel}</span>
                    ) : null}
                    <span>{column.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row, rowIndex) => (
                <tr key={`${section.title}-${rowIndex}`}>
                  {section.columns.map((column) => (
                    <td key={`${section.title}-${rowIndex}-${column.key}`}>
                      {formatReferenceCell(row[column.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {section.notes?.length ? (
          <ul>
            {section.notes.map((note) => <li key={note}>{note}</li>)}
          </ul>
        ) : null}
      </article>
    ))}
  </section>
);

const InterviewQnASection = ({ title, buckets, onOpenResource }) => (
  <section className="learn-qna-wrap">
    <h3>{title}</h3>
    <p>
      This section has only interview questions. Use the resource link button to jump to the related
      lesson in this Learn page.
    </p>
    {buckets.map((bucket) => (
      <article className="learn-qna-card" key={bucket.section}>
        <h4>{bucket.section}</h4>
        <ul>
          {bucket.questions.map((entry) => (
            <li key={entry.id}>
              <span>{entry.question}</span>
              <button
                type="button"
                className="learn-qna-link"
                onClick={() => onOpenResource(entry.resourceTopicId)}
              >
                🔗 Open lesson
              </button>
            </li>
          ))}
        </ul>
      </article>
    ))}
  </section>
);

const PendingTopic = ({ topic }) => (
  <section className="learn-coming-soon">
    <h3>Detailed lesson for this topic is coming soon</h3>
    <p>
      You selected <strong>{topic.title}</strong> under <strong>{topic.techLabel}</strong>.
      We have already added this topic in roadmap, and detailed beginner-friendly notes
      with practical examples will be added next.
    </p>
    <h4>What you should learn in this topic</h4>
    <ul>
      <li>Understand the core idea in simple steps.</li>
      <li>Build one small practical example from scratch.</li>
      <li>Know where this topic is used in real projects.</li>
      <li>Revise important full forms and interview keywords.</li>
    </ul>
  </section>
);

const LearnPage = () => {
  const [state, dispatch] = useReducer(
    learnReducer,
    null,
    () => {
      const selectedTopicId = getInitialTopicId();
      const selectedTopicMeta = TOPIC_LOOKUP[selectedTopicId] || TOPIC_LOOKUP[DEFAULT_TOPIC_ID];
      return {
        expandedTechMap: {
          ...buildInitialExpandedMap(),
          [selectedTopicMeta.techId]: true,
        },
        selectedTechId: selectedTopicMeta.techId,
        selectedTopicId: selectedTopicMeta.id,
      };
    },
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const flattenedTopics = useMemo(
    () => LEARN_TREE.flatMap((tech) => tech.topics.map((topic) => ({
      ...topic,
      techId: tech.id,
      techLabel: tech.label,
      techFullForm: tech.fullForm,
    }))),
    [],
  );

  const searchableTopics = useMemo(() => flattenedTopics.map((topic) => {
    const lesson = HTML_LESSONS[topic.id]
      || CSS_LESSONS[topic.id]
      || JS_LESSONS[topic.id]
      || null;
    const lessonSearchBlob = lesson ? [
      lesson.heading,
      lesson.easySummary,
      ...(lesson.theory || []),
      ...(lesson.practicalTips || []),
      ...flattenReferenceSections(lesson.referenceSections || []),
      ...(lesson.examples || []).flatMap((example) => [example.title, example.description, example.code, example.outputHtml]),
    ] : [];

    return {
      ...topic,
      searchBlob: [
        topic.title,
        topic.id,
        topic.techLabel,
        topic.techFullForm,
        ...lessonSearchBlob,
      ].join(' ').toLowerCase(),
    };
  }), [flattenedTopics]);

  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return [];
    }
    return searchableTopics
      .filter((topic) => topic.searchBlob.includes(query))
      .slice(0, 14);
  }, [searchQuery, searchableTopics]);

  const selectedTopicMeta = TOPIC_LOOKUP[state.selectedTopicId];
  const selectedTopic = selectedTopicMeta || TOPIC_LOOKUP[DEFAULT_TOPIC_ID];
  const selectedLesson = HTML_LESSONS[selectedTopic.id]
    || CSS_LESSONS[selectedTopic.id]
    || JS_LESSONS[selectedTopic.id]
    || null;
  const interviewQnaByTopic = {
    'html-interview-qna': {
      title: 'Interview QnA for HTML',
      buckets: HTML_INTERVIEW_QNA,
    },
    'css-interview-qna': {
      title: 'Interview QnA for CSS',
      buckets: CSS_INTERVIEW_QNA,
    },
    'js-interview-qna': {
      title: 'Interview QnA for JavaScript',
      buckets: JS_INTERVIEW_QNA,
    },
  };
  // Topic-specific interview decks are rendered only on Interview QnA nodes.
  const activeInterviewQna = interviewQnaByTopic[selectedTopic.id] || null;

  const selectedTopicIndex = flattenedTopics.findIndex((item) => item.id === selectedTopic.id);
  const prevTopic = selectedTopicIndex > 0 ? flattenedTopics[selectedTopicIndex - 1] : null;
  const nextTopic = selectedTopicIndex < flattenedTopics.length - 1
    ? flattenedTopics[selectedTopicIndex + 1]
    : null;

  const breadcrumbItems = [
    { label: 'Home', to: '/' },
    { label: 'Learn' },
    { label: selectedTopic.techLabel },
    { label: selectedTopic.title },
  ];

  const seoTitle = `${selectedTopic.title} ${selectedTopic.techLabel} Tutorial for Beginners | Algodoor Learn`;
  const seoDescription = selectedLesson
    ? `${selectedLesson.easySummary} Learn ${selectedTopic.title} in ${selectedTopic.techLabel} with beginner-friendly explanation, practical guidance, and editable examples.`
    : `Learn ${selectedTopic.title} under ${selectedTopic.techFullForm} with step-by-step tutorials and interview-focused revision on Algodoor Learn.`;
  const seoUrl = `/learn/topic/${encodeURIComponent(selectedTopic.id)}`;
  const seoKeywords = [
    `${selectedTopic.techLabel} tutorial`,
    `${selectedTopic.title} tutorial`,
    `${selectedTopic.techLabel} interview questions`,
    'HTML tutorial',
    'CSS tutorial',
    'JavaScript tutorial',
    'Java tutorial',
    'Core Java tutorial',
    'Advanced Java tutorial',
    'OOPS in Java tutorial',
    'Java collections tutorial',
    'Spring Boot tutorial',
    'Microservices tutorial',
    'Java microservices tutorial',
    'TypeScript tutorial',
    'React tutorial',
    'Next.js tutorial',
    'Angular tutorial',
    'Node.js tutorial',
    'MongoDB tutorial',
    'AWS DevOps tutorial',
    'System design tutorial',
    'web development tutorials',
    'frontend tutorial for beginners',
    'full stack tutorial',
  ].join(', ');

  const seoSchema = useMemo(() => {
    const baseUrl = 'https://www.algodoor.com';
    const fullUrl = `${baseUrl}${seoUrl}`;
    const itemList = flattenedTopics.map((topic, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: `${topic.techLabel} ${topic.title} tutorial`,
      url: `${baseUrl}/learn?topic=${topic.id}`,
    }));

    return [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: seoTitle,
        description: seoDescription,
        url: fullUrl,
        inLanguage: 'en-IN',
        isPartOf: {
          '@type': 'WebSite',
          name: 'Algodoor',
          url: baseUrl,
        },
        about: [
          `${selectedTopic.techLabel} tutorial`,
          `${selectedTopic.title} guide`,
          'web development learning',
          'interview preparation',
        ],
        educationalLevel: 'Beginner to Advanced',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Algodoor Learn Tutorials',
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        numberOfItems: flattenedTopics.length,
        itemListElement: itemList,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: `How can I learn ${selectedTopic.techLabel} from beginner level?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `Start with the basics in the ${selectedTopic.techLabel} topic tree, then practice each lesson using editable examples and interview revision sections in Algodoor Learn.`,
            },
          },
          {
            '@type': 'Question',
            name: 'Does this portal include interview questions for web development?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. The Learn portal includes interview-focused QnA sections for core tracks like HTML and CSS, connected with resource lesson links for quick revision.',
            },
          },
          {
            '@type': 'Question',
            name: 'Which tutorials are available in Algodoor Learn?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Algodoor Learn provides tutorials for HTML, CSS, JavaScript, TypeScript, React, Next.js, Angular, Node.js, MongoDB, AWS DevOps, and System Design.',
            },
          },
        ],
      },
      generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Learn', url: '/learn' },
        { name: selectedTopic.techLabel, url: seoUrl },
        { name: selectedTopic.title, url: seoUrl },
      ]),
    ];
  }, [flattenedTopics, selectedTopic.techLabel, selectedTopic.title, seoDescription, seoTitle, seoUrl]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    // Keep the URL in sync with selected topic so shared links open exact lesson.
    const targetPath = `/learn/topic/${encodeURIComponent(selectedTopic.id)}`;
    if (window.location.pathname !== targetPath) {
      window.history.replaceState(null, '', targetPath);
    }
  }, [selectedTopic.id]);

  const openResourceTopic = (topicId) => {
    const topic = TOPIC_LOOKUP[topicId];
    if (!topic) {
      return;
    }
    dispatch({
      type: 'SELECT_TOPIC',
      techId: topic.techId,
      topicId,
    });
  };

  const openSearchResult = (topic) => {
    dispatch({
      type: 'SELECT_TOPIC',
      techId: topic.techId,
      topicId: topic.id,
    });
    setSearchQuery('');
    setMobileSidebarOpen(false);
  };

  return (
    <div className="learn-page">
      <SEOMeta
        title={seoTitle}
        description={seoDescription}
        url={seoUrl}
        type="WebPage"
        socialTitle={seoTitle}
        socialDescription={seoDescription}
        schemaMarkup={seoSchema}
        keywords={seoKeywords}
      />

      <div className="gradient__bg">
        <Navbar />
      </div>
      <Breadcrumbs items={breadcrumbItems} />

      <section className="learn-shell">
        <header className="learn-header">
          <div className="learn-header-copy">
            <span className="learn-header-kicker">Algodoor Learning Hub</span>
            <h1>Structured lessons for modern web development</h1>
            <p>
              Follow a clear topic-by-topic learning path with guided explanations, editable
              examples, and focused practice designed for serious skill building.
            </p>
          </div>
          <div className="learn-header-highlights">
            <div className="learn-header-stat">
              <strong>Topic Tree</strong>
              <span>Move from fundamentals to interview-focused revision.</span>
            </div>
            <div className="learn-header-stat">
              <strong>Live Practice</strong>
              <span>Edit code, review output instantly, and test concepts faster.</span>
            </div>
          </div>
        </header>

        <section className="learn-search-shell">
          <label className="learn-search-label" htmlFor="learn-topic-search">Search across topics, tags, attributes, and lesson content</label>
          <input
            id="learn-topic-search"
            className="learn-search-input"
            type="search"
            placeholder="Try: img, iframe, legend, flexbox, url encoding, caching"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
          {searchQuery.trim() ? (
            <div className="learn-search-results">
              {searchResults.length ? searchResults.map((topic) => {
                return (
                  <button
                    type="button"
                    key={`${topic.techId}-${topic.id}`}
                    className="learn-search-result"
                    onClick={() => openSearchResult(topic)}
                  >
                    <span className="learn-search-result-top">
                      <strong>{topic.title}</strong>
                      <span>{topic.techLabel}</span>
                    </span>
                  </button>
                );
              }) : <p className="learn-search-empty">No matching topics found for this search.</p>}
            </div>
          ) : null}
        </section>

        <button
          type="button"
          className="learn-mobile-toggle"
          onClick={() => setMobileSidebarOpen((prev) => !prev)}
        >
          {mobileSidebarOpen ? 'Hide Topics' : 'Show Topics'}
        </button>

        <div className="learn-layout">
          <aside className={`learn-sidebar ${mobileSidebarOpen ? 'open' : ''}`}>
            <h2>Topic Tree</h2>
            <p className="learn-sidebar-subtext">Click branch to expand topics.</p>
            <div className="learn-tree-wrap">
              {LEARN_TREE.map((tech) => {
                const isExpanded = !!state.expandedTechMap[tech.id];
                const TechIcon = TECH_ICON_MAP[tech.id] || FiBookOpen;
                return (
                  <div className="learn-tech-branch" key={tech.id}>
                    <button
                      type="button"
                      className="learn-tech-toggle"
                      onClick={() => dispatch({ type: 'TOGGLE_TECH', techId: tech.id })}
                    >
                      <span>{isExpanded ? '▾' : '▸'}</span>
                      <TechIcon className="learn-tech-icon" aria-hidden="true" />
                      <span>{tech.label}</span>
                    </button>
                    <p className="learn-tech-fullform">{tech.fullForm}</p>

                    {isExpanded && (
                      <ul className="learn-topic-list">
                        {tech.topics.map((topic) => {
                          const isActive = topic.id === state.selectedTopicId;
                          const isImportant = IMPORTANT_TOPIC_IDS.has(topic.id);
                          return (
                            <li key={topic.id}>
                              <button
                                type="button"
                                className={`learn-topic-btn ${isActive ? 'active' : ''}`}
                                onClick={() => {
                                  dispatch({
                                    type: 'SELECT_TOPIC',
                                    techId: tech.id,
                                    topicId: topic.id,
                                  });
                                  setMobileSidebarOpen(false);
                                }}
                              >
                                <span className="learn-topic-title-row">
                                  <span className="learn-topic-title">{topic.title}</span>
                                  {isImportant ? (
                                    <FiAlertCircle
                                      className="learn-topic-important"
                                      aria-label="Important topic"
                                      title="Important topic"
                                    />
                                  ) : null}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </aside>

          <main className="learn-content">
            <div className="learn-content-top">
              <div className="learn-topic-pill">{selectedTopic.techLabel}</div>
              <h2>{selectedLesson ? selectedLesson.heading : selectedTopic.title}</h2>
              <p>
                {selectedLesson
                  ? selectedLesson.easySummary
                  : `${selectedTopic.techFullForm} topic roadmap is ready. Detailed lesson notes are being expanded.`}
              </p>
            </div>

            {activeInterviewQna ? (
              <InterviewQnASection
                title={activeInterviewQna.title}
                buckets={activeInterviewQna.buckets}
                onOpenResource={openResourceTopic}
              />
            ) : selectedLesson ? (
              <>
                <section className="learn-theory">
                  <h3>Concepts you must master</h3>
                  {selectedLesson.theory.map((line) => <p key={line}>{line}</p>)}

                  <h3>Practical guidance</h3>
                  <ul>
                    {selectedLesson.practicalTips.map((tip) => <li key={tip}>{tip}</li>)}
                  </ul>
                </section>

                {selectedLesson.referenceSections?.length ? (
                  <LessonReferenceSections sections={selectedLesson.referenceSections} />
                ) : null}

                <LessonExamples examples={selectedLesson.examples} />
              </>
            ) : (
              <PendingTopic topic={selectedTopic} />
            )}

            <div className="learn-nav-actions">
              <button
                type="button"
                onClick={() => prevTopic && dispatch({
                  type: 'SELECT_TOPIC',
                  techId: prevTopic.techId,
                  topicId: prevTopic.id,
                })}
                disabled={!prevTopic}
              >
                Previous Topic
              </button>
              <span>
                Topic {selectedTopicIndex + 1} of {flattenedTopics.length}
              </span>
              <button
                type="button"
                onClick={() => nextTopic && dispatch({
                  type: 'SELECT_TOPIC',
                  techId: nextTopic.techId,
                  topicId: nextTopic.id,
                })}
                disabled={!nextTopic}
              >
                Next Topic
              </button>
            </div>
          </main>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LearnPage;
